import { Guest } from "@/types/Guest";
import { IconArmchair, IconPlus } from "@tabler/icons-react";
import { useNavigate as useRouter, useParams } from "react-router-dom";
import React, { createContext, lazy, Suspense, useContext, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Plugin } from "@/types/enums/Plugin";
import { useCapabilitiesOptional } from "@/providers/CapabilitiesProvider";
import { useEventContextOptional } from "@/providers/EventProvider";
import { usePreviewWriteGuard } from "@/lib/preview/usePreviewWriteGuard";
import {
  printedSeatOf,
  seatLabelText,
  seatPlaceText,
  SEAT_TEXT_SEPARATOR,
  type PrintedSeat,
  type SeatingTranslator,
} from "@/lib/seating/seatNumber";
import { OverflowTooltip } from "../OverflowTooltip";

const PSeatCellEditor = lazy(() =>
  import("@/components/PSeatCellEditor/PSeatCellEditor").then((m) => ({
    default: m.PSeatCellEditor,
  })),
);

/** The seat cell's value: the primary seat, the honest total, and the identity
 *  its editor writes with. Assembled in guestCellValue so no extra prop has to
 *  be threaded through TableCellContent. */
export type SeatingCellValue =
  | {
      /** The PRIMARY seat — display only. See the note on `seatCount` below. */
      seat: NonNullable<Guest["seatInfo"]> | null;
      seatCount?: number | null;
      guestId: string | null;
      guestName?: string | null;
      guestType?: string | null;
      /** A companion's parent id, as the flat list carries it. */
      mainGuest?: string | null;
      /** …and as the hydrated lane carries it. */
      mainGuestId?: string | null;
      numCompanions?: number | null;
      /** Ids + names only; the flat list projects nothing else. */
      companions?: { guestId: string; firstName: string | null; lastName: string | null }[];
    }
  /** Older callers (and the filter row) still hand over the bare seat or null. */
  | (NonNullable<Guest["seatInfo"]> & { seatCount?: number | null })
  | null;

/**
 * The chip text: "RT 1 #4" as always — the number is the PRINTED one (the
 * server's `seatNumber` on `seatInfo`, else `seatIndex plus one`) — and on a theatre
 * block with row labels "Parkett · Row B · Seat 3", because "#3" alone repeats
 * once per row there. A piece in a block (seats.io Teil 2, Zug 3) reads its area
 * and block first — "Block A · RT 1 #4" — from `seatInfo.seatBlock`/`seatArea`;
 * without a block the chip is exactly today's.
 */
function seatChipText(t: SeatingTranslator, furnitureName: string, printed: PrintedSeat): string {
  return seatPlaceText(
    printed,
    printed.seatRow !== null
      ? `${furnitureName} · ${seatLabelText(t, printed)}`
      : `${furnitureName} #${printed.seatNumber}`,
  );
}

/**
 * The chip text in parts, for a seat in a block (seats.io Teil 2, Zug 3, audit
 * r1 FX-6, r2 FY-5). "Parkett · Block C" in front of the piece makes the chip
 * long, and a narrow column must still show the seat number — the part an
 * operator reads. The parts give way strictly in order — the place, the piece,
 * a theatre seat's row, the word "Seat" — and the number last, with an
 * ellipsis, only when nothing else is left. Every separator belongs to the part
 * before it, so it goes with that part and never dangles. `null` without a
 * block: that chip stays one text node, exactly today's.
 *
 * <p>A theatre seat's "Row B · Seat 12" is split at our separator (every
 * locale writes the row first, then the seat, joined by " · "), and the seat at
 * its number when the number does not lead ("Seat 12" → "Seat", "12"; "12.
 * ülőhely" stays whole). A label that does not split stays whole.
 */
function seatChipParts(
  t: SeatingTranslator,
  furnitureName: string,
  printed: PrintedSeat,
): { cuts: { testId: string; text: string; sep: string }[]; seat: string } | null {
  const place = seatPlaceText(printed, "");
  if (place === "") return null;
  const cuts = [{ testId: "seat-chip-place", text: place, sep: SEAT_TEXT_SEPARATOR }];
  if (printed.seatRow === null) {
    cuts.push({ testId: "seat-chip-piece", text: furnitureName, sep: " " });
    return { cuts, seat: `#${printed.seatNumber}` };
  }
  cuts.push({ testId: "seat-chip-piece", text: furnitureName, sep: SEAT_TEXT_SEPARATOR });
  const label = seatLabelText(t, printed);
  const at = label.indexOf(SEAT_TEXT_SEPARATOR);
  if (at <= 0) return { cuts, seat: label };
  cuts.push({ testId: "seat-chip-row", text: label.slice(0, at), sep: SEAT_TEXT_SEPARATOR });
  const seat = label.slice(at + SEAT_TEXT_SEPARATOR.length);
  const n = seat.lastIndexOf(String(printed.seatNumber));
  const word = n > 0 ? seat.slice(0, n) : "";
  if (word.trim() === "") return { cuts, seat };
  const wordText = word.trimEnd();
  cuts.push({ testId: "seat-chip-seat-word", text: wordText, sep: word.slice(wordText.length) });
  return { cuts, seat: seat.slice(n) };
}

/** The attribute every part that may be cut carries — what the cell's tooltip watches (FY-5). */
const SEAT_CUT = "data-seat-cut";
const SEAT_CUT_SELECTOR = `[${SEAT_CUT}]`;

/** A part that gives way before the ones after it; its separator goes with it. */
function CutPart({ testId, text, sep }: { testId: string; text: string; sep: string }) {
  return (
    <span {...{ [SEAT_CUT]: "" }} className="truncate min-w-0">
      <span data-testid={testId}>{text}</span>
      <span className="whitespace-pre">{sep}</span>
    </span>
  );
}

/**
 * One level per part: the part shrinks, the rest beside it does not but is
 * never wider than the row (`shrink-0 max-w-full`) — a strict order, no part
 * gives way while the one before it still has room.
 */
function GiveWay({ cuts, last }: { cuts: { testId: string; text: string; sep: string }[]; last: React.ReactNode }) {
  const [first, ...rest] = cuts;
  if (!first) return <>{last}</>;
  return (
    <>
      <CutPart {...first} />
      {rest.length === 0 ? last : (
        <span className="flex shrink-0 max-w-full">
          <GiveWay cuts={rest} last={last} />
        </span>
      )}
    </>
  );
}

/** The chip label: one text node without a block; with one, its parts given up in order (FY-5). */
function SeatChipLabel({ t, furnitureName, printed }: { t: SeatingTranslator; furnitureName: string; printed: PrintedSeat }) {
  const parts = seatChipParts(t, furnitureName, printed);
  if (!parts) return <>{seatChipText(t, furnitureName, printed)}</>;
  return (
    <span className="flex min-w-0" data-testid="seat-chip-text">
      <GiveWay
        cuts={parts.cuts}
        last={<span {...{ [SEAT_CUT]: "" }} className="truncate shrink-0 max-w-full" data-testid="seat-chip-seat">{parts.seat}</span>}
      />
    </span>
  );
}

/**
 * The whole seat text a cut chip hides — the SEATING cell's tooltip (FY-5):
 * "Parkett · Block C · RT 12 #4 · +2 more". `undefined` for a seat without a
 * block (that cell is today's, with no tooltip) and for an empty cell.
 */
export function seatCellTitle(t: SeatingTranslator, value: SeatingCellValue): string | undefined {
  const { seat, seatCount } = readValue(value);
  if (!seat) return undefined;
  const printed = printedSeatOf(seat);
  if (!seatChipParts(t, seat.furnitureName, printed)) return undefined;
  const full = seatChipText(t, seat.furnitureName, printed);
  const extra = typeof seatCount === "number" ? Math.max(0, seatCount - 1) : 0;
  return extra > 0 ? `${full}${SEAT_TEXT_SEPARATOR}${t("seats.moreSeats", { count: extra })}` : full;
}

/** Set by a cell that already shows the whole text as its tooltip, so the chip does not add a second one. */
const SeatTitleInCell = createContext(false);

/**
 * The SEATING cell's overflow tooltip (TableCell): the whole seat text as its
 * `cellTitle`, shown when ANY part of the chip is cut — the parts cut inside a
 * box that itself never overflows. Without a block it is exactly the plain
 * cell's tooltip (no text, so none).
 */
export function SeatCellOverflow({ value, children }: { value: unknown; children: React.ReactNode }) {
  const t = useTranslations("seating");
  const title = seatCellTitle(t, value as SeatingCellValue);
  if (title === undefined) return <OverflowTooltip text={undefined}>{children}</OverflowTooltip>;
  return (
    <SeatTitleInCell.Provider value>
      <OverflowTooltip text={title} preferText cutSelector={SEAT_CUT_SELECTOR}>{children}</OverflowTooltip>
    </SeatTitleInCell.Provider>
  );
}

type TCCSeatingProps = {
  value: SeatingCellValue;
  /**
   * Activity rollup: how many seats the guest holds in total. `value.seat` is
   * only the PRIMARY seat (lowest furniture id), so without this the cell
   * asserted "one seat" for a guest sitting in three rooms — which is how the
   * rail, this column and the hover card each ended up naming a different table.
   *
   * The ROOM name is deliberately not rendered here: it would need the
   * `seatRoomIds` option-source resolution that the separate "Room" column
   * already performs, and duplicating that into a leaf cell is its own change.
   * The overflow marker is what stops this cell from lying.
   */
  isEditing: boolean;
  /**
   * A seat write LANDED. Not `onChangeValue`: a chair is not a field of the
   * guest — it is a row in a furniture document, written in its own transaction
   * with its own mail — and the pending-edit batch has no case for it and would
   * drop it in silence. Same channel IMAGE uses, and for the same reason.
   */
  onValuePersisted?: () => void;
  isFilter?: boolean;
  viewOnly?: boolean;
};

/** Normalizes the two shapes this cell has carried. */
function readValue(value: SeatingCellValue): {
  seat: (NonNullable<Guest["seatInfo"]> & { seatCount?: number | null }) | null;
  seatCount: number | null;
  guestId: string | null;
  guestName: string;
  partyHostId: string | null;
  numCompanions: number;
  companions: { guestId: string; firstName: string | null; lastName: string | null }[];
} {
  const empty = {
    seat: null,
    seatCount: null,
    guestId: null,
    guestName: "",
    partyHostId: null,
    numCompanions: 0,
    companions: [],
  };
  // `typeof`, not just truthiness: `in` on a string or a number is a TypeError,
  // and this cell renders from whatever the row hands it. Nothing reaches it in
  // that shape today (SEATING is not filterable, and `guestCellValue` is the
  // only producer) — which is exactly why a wrong value would arrive one day
  // with nothing between it and the whole table going blank.
  if (!value || typeof value !== "object") return empty;
  if ("seat" in value) {
    const host =
      value.guestType === "companion"
        ? value.mainGuest || value.mainGuestId || null
        : value.guestId;
    return {
      seat: value.seat ? { ...value.seat, seatCount: value.seatCount ?? null } : null,
      seatCount: value.seatCount ?? null,
      guestId: value.guestId,
      guestName: value.guestName ?? "",
      partyHostId: host,
      // The chips, and only the chips. `?? value.numCompanions` stood here as
      // "the answer a companion row has about its own family" — it never ran:
      // `guestCellValue` always emits an array, so the left side is a number
      // and `??` does not fall through on 0. The family question is answered by
      // the party read in the editor instead.
      numCompanions: value.companions?.length ?? 0,
      companions: value.companions ?? [],
    };
  }
  return { ...empty, seat: value, seatCount: value.seatCount ?? null };
}

export const TCCSeating: React.FC<TCCSeatingProps> = ({
  value,
  isEditing,
  onValuePersisted,
  isFilter = false,
  viewOnly = false,
}) => {
  const t = useTranslations("seating");
  const router = useRouter();
  const { eventId = "" } = useParams<{ eventId: string }>();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  // Optional readers: this cell is rendered by tables that are not event-scoped
  // (and by the filter row), where the throwing ones would take the page down.
  const capabilities = useCapabilitiesOptional();
  const eventCtx = useEventContextOptional();
  const { blockIfPreviewing } = usePreviewWriteGuard();

  const { seat, seatCount, guestId, guestName, partyHostId, numCompanions, companions } =
    readValue(value);

  // Three gates, all of which must be true before a chair can be written from
  // here: the role may write seats, the event HAS a seating plan, and this is a
  // real row rather than the filter row.
  const canEdit =
    !isFilter &&
    !viewOnly &&
    !!guestId &&
    !!eventId &&
    !!capabilities?.can("seating:write") &&
    // …and READ: every room, table and chair the editor works from comes from
    // `/init`, which is gated on `seating:read`. The `implies` list that pairs
    // the two is FRONTEND metadata, expanded when a role template is saved
    // through the editor — a template written straight through the API carries
    // exactly the keys it was given, and the backend has no implication table.
    // Without this the editor opens on a 403 and reports it as "no rooms".
    !!capabilities?.can("seating:read") &&
    !!eventCtx?.event?.plugins?.includes(Plugin.SEATING);

  const extraSeats =
    typeof seatCount === "number" ? Math.max(0, seatCount - 1) : 0;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!seat) return;
    // `seat` (0-based) turns this from "open that table" into "show me THIS
    // seat": the plan centres on it and the seat pulses.
    router(`/event/${eventId}/seating?furnitureId=${seat.furnitureId}&seat=${seat.seatIndex}`);
  };

  const openEditor = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    // Same guard every other write in a cell takes first: in a read-only "preview
    // as role" session the token masks the write, and a 403 would read as "no
    // permission" rather than "you are previewing".
    if (blockIfPreviewing()) return;
    // A TOGGLE, not an open: the editor's click-outside handler deliberately
    // ignores this anchor (it would otherwise race its own reopen), so this is
    // the only way a second click on the trigger can close what the first
    // opened.
    setEditorOpen((open) => !open);
  };

  const editor = editorOpen && guestId ? (
    <Suspense fallback={null}>
      <PSeatCellEditor
        eventId={eventId}
        guestId={guestId}
        guestName={guestName}
        partyHostId={partyHostId}
        companionCount={numCompanions}
        companions={companions}
        anchorRef={anchorRef}
        onPersisted={() => onValuePersisted?.()}
        onClose={() => setEditorOpen(false)}
      />
    </Suspense>
  ) : null;

  // In the inline-edit mode the cell used to render NOTHING, which is why the
  // column went blank the moment the operator started editing. It is the edit
  // surface now — the whole cell opens the editor.
  // A seat in a block may be cut (place first, see seatChipParts); without a
  // block every class below is today's.
  const placed = seat !== null && seatChipParts(t, seat.furnitureName, printedSeatOf(seat)) !== null;
  // Outside a table cell (the guest details) the chip brings its own tooltip.
  const titleInCell = useContext(SeatTitleInCell);

  if (isEditing) {
    if (!canEdit) return <></>;
    return (
      <div ref={anchorRef} className="flex flex-wrap gap-1">
        <button
          type="button"
          data-testid="seat-cell-edit"
          onClick={openEditor}
          className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md surface-tertiary border outline-tertiary body-3-medium font-secondary hover:surface-quaternary focus-ring transition-all${placed ? " min-w-0 max-w-full" : ""}`}
        >
          <IconArmchair size={12} />
          {seat ? <SeatChipLabel t={t} furnitureName={seat.furnitureName} printed={printedSeatOf(seat)} /> : t("cell.assign")}
        </button>
        {editor}
      </div>
    );
  }

  if (!seat) {
    // An empty cell used to render nothing at all — and an editor nobody can
    // reach is not an editor. The affordance appears only for a role that may
    // actually use it.
    if (!canEdit) return <></>;
    return (
      <div ref={anchorRef} className="flex flex-wrap gap-1">
        <button
          type="button"
          data-testid="seat-cell-assign"
          onClick={openEditor}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md body-3-medium font-tertiary hover:surface-tertiary hover:font-secondary focus-ring transition-all"
        >
          <IconPlus size={12} />
          {t("cell.assign")}
        </button>
        {editor}
      </div>
    );
  }

  const row = (
    <div ref={anchorRef} className={placed ? "group/seat flex items-center gap-1 min-w-0" : "group/seat flex flex-wrap items-center gap-1"}>
      <span
        onClick={handleClick}
        data-testid={placed ? "seat-chip" : undefined}
        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md surface-tertiary border border-transparent body-3-medium font-secondary cursor-pointer hover:surface-quaternary hover:border hover:outline-tertiary hover:shadow-sm transition-all${placed ? " min-w-0 max-w-full" : ""}`}
      >
        <IconArmchair size={12} className={placed ? "shrink-0" : undefined} />
        <SeatChipLabel t={t} furnitureName={seat.furnitureName} printed={printedSeatOf(seat)} />
      </span>
      {extraSeats > 0 && (placed ? (
        // In a block row nothing wraps, so the marker stays short ("+2"): the
        // words would take the chip's room (FY-5). They are read out, and the
        // cell's tooltip says them.
        <span className="shrink-0 inline-flex items-center px-2 py-0.5 rounded-md surface-tertiary body-3-medium font-tertiary" data-testid="seat-chip-more">
          <span aria-hidden>+{extraSeats}</span>
          <span className="sr-only">{t("seats.moreSeats", { count: extraSeats })}</span>
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-0.5 rounded-md surface-tertiary body-3-medium font-tertiary">
          {t("seats.moreSeats", { count: extraSeats })}
        </span>
      ))}
      {/* The chip keeps its jump to the plan — that is what it has always done
          and people use it. Editing gets its own small target, which appears on
          hover and on keyboard focus (never only on hover). */}
      {canEdit && (
        <button
          type="button"
          data-testid="seat-cell-edit"
          onClick={openEditor}
          aria-label={t("cell.edit")}
          title={t("cell.edit")}
          className="p-0.5 rounded font-tertiary opacity-0 group-hover/seat:opacity-100 focus:opacity-100 focus-ring hover:font-secondary transition-opacity"
        >
          <IconArmchair size={12} />
        </button>
      )}
      {editor}
    </div>
  );
  if (!placed || titleInCell) return row;
  return (
    <OverflowTooltip text={seatCellTitle(t, value)} preferText cutSelector={SEAT_CUT_SELECTOR}>{row}</OverflowTooltip>
  );
};
